import React, { FunctionComponent, useRef, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { equals, find } from 'ramda';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ISurvey } from '../../../../common/models/survey/ISurvey';
import SearchInput from '../../../../components/SearchInput';
import { fetchReqBuilderSurveysRoutine, setReqSurveyRoutine, fetchPositionLastSurveyRoutine } from '../../routines';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import Table from '../../../../components/Table';
import { getColumns } from './utils';
import { ISelectedSurvey } from '../../../../common/models/survey/ISelectedSurvey';
import { ISelectedPosition } from '../../../../common/models/position/ISelectedPosition';
import { showSurveyBuilderRoutine, hideSurveyBuilderRoutine } from '../../../SurveyBuilder/routines';
import ModalWindow from '../../../../components/ModalWindow';
import SurveyBuilder, { ISurveyBuilderHandle } from '../../../SurveyBuilder/containers/SurveyBuilder';
import { IFilter } from '../../../../common/models/filter/IFilter';
import RegularButton from '../../../../components/RegularButton';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import { IRefReqSurveyFilter } from '../../../../common/models/filter/IRefReqSurveyFilter';
import Illustration from '../../../../components/Illustration';
import styles from './styles.module.scss';

interface IProps {
  selectedPosition: ISelectedPosition | null;
  surveys: ISurvey[];
  total: number;
  lastSurvey: ISelectedSurvey | null;
  selectedSurvey: ISelectedSurvey | null;
  isSurveySaved: boolean;
  fetchSurveys: IBindingCallback1<IRefReqSurveyFilter>;
  setSurvey: IBindingCallback1<ISelectedSurvey | null>;
  fetchLastSurvey: IBindingCallback1<string | null>;
  showSurveyBuilder: IBindingCallback1<string>;
  hideSurveyBuilder: IBindingAction;
  loading: boolean;
}

const Step4: FunctionComponent<IProps> = ({
  surveys,
  total,
  lastSurvey,
  isSurveySaved,
  selectedSurvey,
  selectedPosition,
  fetchSurveys,
  setSurvey,
  fetchLastSurvey,
  showSurveyBuilder,
  hideSurveyBuilder,
  loading
}) => {
  const defaultSort = [{ id: 'lastSendDate', desc: true }];
  const defaultFilter: IRefReqSurveyFilter = {
    from: 0,
    to: 25,
    input: '',
    sort: defaultSort,
    filter: [],
    surveyId: lastSurvey ? lastSurvey.id : undefined
  };

  const [filter, setFilter] = useState<IFilter>(defaultFilter);
  const [isSurveyBuilderShown, setIsSurveyBuilderShown] = useState(false);
  const [totalCount, setTotalCount] = useState(total);
  const surveyBuilderRef = useRef<ISurveyBuilderHandle>(null);

  useEffect(() => setTotalCount(total), [total]);

  const confirmSurveyBuilderClose = () => surveyBuilderRef.current?.confirmClose();

  const clearFilters = () => setFilter(defaultFilter);

  const updateFilter = (newFilter: Partial<IRefReqSurveyFilter>) => {
    const updatedFilter = { ...filter, ...newFilter };

    if (!equals(updatedFilter, filter)) {
      setFilter(updatedFilter);
    }
  };

  const loadData = (from: number, to: number) => {
    fetchSurveys({ ...filter, from, to });
  };

  const onRowSelected = ({ id, name }: ISurvey, isSelected: boolean) => (
    setSurvey(isSelected ? { id, name } : null)
  );

  const onSurveyBuilderHide = () => {
    hideSurveyBuilder();
    setIsSurveyBuilderShown(false);
  };

  const onSurveyBuilderShow = (id: string) => {
    showSurveyBuilder(id);
    setIsSurveyBuilderShown(true);
  };

  useEffect(() => {
    fetchSurveys(filter);
  }, [filter]);

  useEffect(() => {
    updateFilter({ surveyId: lastSurvey ? lastSurvey.id : undefined });
  }, [lastSurvey]);

  useEffect(() => {
    if (isSurveySaved) clearFilters();
  }, [isSurveySaved]);

  useEffect(() => {
    if (selectedPosition?.id) {
      fetchLastSurvey(selectedPosition.id);
    }

    return () => fetchLastSurvey(null);
  }, []);

  const selectedSurveyData = find(({ id }) => id === selectedSurvey?.id, surveys);

  const noDataPlaceholder = (
    <Illustration
      variant="binoculars"
      height="22rem"
      header={<FormattedMessage id="resultsNotFound" defaultMessage="Hmm... no matches found" />}
      description={(
        <FormattedMessage
          id="resultsNotFoundHint"
          defaultMessage="Try searching for something else"
        />
      )}
    />
  );

  return (
    <div className={styles.step4}>
      <ModalWindow
        isShown={isSurveyBuilderShown}
        onHide={confirmSurveyBuilderClose}
        contentClassName="big-modal sb-modal-wrapper"
      >
        <SurveyBuilder ref={surveyBuilderRef} hideSurveyBuilder={onSurveyBuilderHide} />
      </ModalWindow>
      <header className={styles.step4Header}>
        <div className="d-flex flex-column">
          <span className="d-inline-block mb-2">
            <FormattedMessage
              id="step4.headerText"
              defaultMessage="Select which survey references will answer"
            />
          </span>
          <span className="t-14">
            <FormattedMessage
              id="step4.headerDesc"
              defaultMessage="Click the preview icon to view/edit questions, or create your own survey"
            />
          </span>
        </div>
        <div className={styles.rightHeader}>
          <SearchInput
            intlId="step4.placeholder"
            defaultPlaceholder="Search for surveys"
            value={filter.input}
            onChange={val => updateFilter({ input: val })}
          />
          <RegularButton
            variant="tertiary"
            onClick={() => setIsSurveyBuilderShown(true)}
            textIntlId="createNewSurvey"
            icon={<PlusIcon />}
          />
        </div>
      </header>
      <Table
        config={{
          columns: getColumns(onSurveyBuilderShow, lastSurvey ? [lastSurvey] : []),
          data: { items: surveys, total: totalCount },
          onLoad: loadData,
          loading,
          onFilterUpdated: updateFilter,
          sortingEnabled: true,
          filteringEnabled: true,
          rowSelection: 'single',
          selectionType: 'combine',
          onRowSelected,
          defaultSelected: selectedSurveyData ? [selectedSurveyData] : undefined,
          noDataPlaceholder,
          defaultSorted: defaultSort
        }}
      />
    </div>
  );
};

const mapStateToProps = ({
  requestBuilder: { selectedPosition, surveys, totalSurveys, lastSurvey, selectedSurvey, loading },
  surveyBuilder: { isSaved }
}: IAppState) => ({
  selectedPosition,
  surveys,
  total: totalSurveys,
  lastSurvey,
  selectedSurvey,
  isSurveySaved: isSaved,
  loading
});

const mapDispachToProps = {
  fetchSurveys: fetchReqBuilderSurveysRoutine,
  setSurvey: setReqSurveyRoutine,
  fetchLastSurvey: fetchPositionLastSurveyRoutine,
  showSurveyBuilder: showSurveyBuilderRoutine,
  hideSurveyBuilder: hideSurveyBuilderRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(Step4);
